import { useEffect, useMemo, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { lightBands, overlapsAog, sunTimes } from '@jupi/shared'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'
import { aircraftColor, formatRange, formatTime, useClubLocation } from '@jupi/ui'
import type { Aircraft, Booking } from '../../api/queries'
import { dateOf } from '../../components/flightLog/FlightLogCard'
import { BARBER_POLE, bookingIsAog, type AircraftStatus } from './aircraftStatus'
import { BookingHover, useBookingPeek } from './BookingPeek'
import { BOOKING_TYPE_ICONS, typeOf } from './bookingType'
import { LIGHT_TINT } from './daylight'

// One lane per aircraft, time across the top -- the fleet-at-a-glance view
// the club actually works from, and the thing bookings.php's per-aircraft
// table could never show.
//
// This is hand-built rather than pulled from a library. Every free calendar
// (react-big-calendar included) ships month/week/day grids but puts the
// resource-timeline view behind a paid licence, and the club's layout has a
// requirement none of them cover anyway: a standby option renders as a
// hatched strip along the BOTTOM EDGE of the lane it is queued behind,
// never as a competing full-height event. An option does not occupy the
// aircraft, so it must not look like it does.

const DAY_START_HOUR = 6
const DAY_END_HOUR = 22
const LANE_HEIGHT = 56
const OPTION_STRIP_HEIGHT = 8
const LABEL_WIDTH = 132
const SNAP_MS = 30 * 60_000
// A finger must rest this long before a drag selects time; moving sooner
// scrolls the grid sideways as before.
const LONG_PRESS_MS = 250

interface Selection {
  registration: string
  anchor: number
  current: number
}

export interface FleetDayGridProps {
  date: Date
  aircraft: Aircraft[]
  bookings: Booking[]
  // Per registration: AOG block and restrictions, from the aircraft's defects.
  statuses?: Map<string, AircraftStatus>
  onOpenDefects?: (registration: string) => void
  onSelectBooking?: (booking: Booking) => void
  onSelectSlot?: (registration: string, startsAt: Date, endsAt?: Date) => void
}

export default function FleetDayGrid({
  date,
  aircraft,
  bookings,
  statuses,
  onOpenDefects,
  onSelectBooking,
  onSelectSlot: onSelectSlotProp,
}: FleetDayGridProps) {
  const theme = useTheme()
  const { t } = useTranslation()
  const peek = useBookingPeek(onSelectBooking)
  const place = useClubLocation()

  const { dayStart, dayEnd, hours } = useMemo(() => {
    const start = new Date(date)
    start.setHours(DAY_START_HOUR, 0, 0, 0)
    const end = new Date(date)
    end.setHours(DAY_END_HOUR, 0, 0, 0)
    const labels: number[] = []
    for (let h = DAY_START_HOUR; h <= DAY_END_HOUR; h++) labels.push(h)
    return { dayStart: start, dayEnd: end, hours: labels }
  }, [date])

  const spanMs = dayEnd.getTime() - dayStart.getTime()
  const dayKey = dateOf(dayStart.toISOString())

  // Day, twilight and night across the visible hours, and the sun's own
  // times for the ruler. Offline: sunTimes is plain arithmetic.
  const bands = useMemo(() => lightBands(dayStart.getTime(), dayEnd.getTime(), place), [dayStart, dayEnd, place])
  const sun = useMemo(() => sunTimes(dayKey, place.lat, place.lon).sun, [dayKey, place])
  const sunMarks = useMemo(
    () =>
      sun.state === 'normal'
        ? [
            { iso: sun.rise!, icon: 'mdi:weather-sunset-up', label: t('calendar.sunrise', { time: formatTime(sun.rise!) }) },
            { iso: sun.set!, icon: 'mdi:weather-sunset-down', label: t('calendar.sunset', { time: formatTime(sun.set!) }) },
          ].filter((mark) => Date.parse(mark.iso) >= dayStart.getTime() && Date.parse(mark.iso) <= dayEnd.getTime())
        : [],
    [sun, dayStart, dayEnd, t],
  )
  const bandStyle = (band: { from: number; to: number; light: 'day' | 'twilight' | 'night' }) => ({
    position: 'absolute' as const,
    top: 0,
    bottom: 0,
    left: `${((band.from - dayStart.getTime()) / spanMs) * 100}%`,
    width: `${((band.to - band.from) / spanMs) * 100}%`,
    bgcolor: LIGHT_TINT[band.light],
    pointerEvents: 'none' as const,
  })

  // Slide-to-select. A mouse drags immediately; a finger long-presses first so
  // the grid can still be scrolled sideways.
  const [selection, setSelection] = useState<Selection | null>(null)
  const selectionRef = useRef<Selection | null>(null)
  const pending = useRef<{ timer: number; x: number; y: number } | null>(null)
  const suppressClick = useRef(false)

  const update = (next: Selection | null) => {
    selectionRef.current = next
    setSelection(next)
  }

  // Once selecting on a touch screen, stop the browser turning the move into
  // a scroll. React's touch handlers are passive, so this needs a native one.
  const selecting = selection !== null
  useEffect(() => {
    if (!selecting) return
    const block = (event: TouchEvent) => {
      if (event.cancelable) event.preventDefault()
    }
    window.addEventListener('touchmove', block, { passive: false })
    return () => window.removeEventListener('touchmove', block)
  }, [selecting])

  const floorAt = (rect: DOMRect, clientX: number) => {
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
    return dayStart.getTime() + Math.floor((ratio * spanMs) / SNAP_MS) * SNAP_MS
  }

  const finish = (commit: boolean) => {
    if (pending.current) {
      window.clearTimeout(pending.current.timer)
      pending.current = null
    }
    const current = selectionRef.current
    if (!current) return
    update(null)
    if (!commit || !onSelectSlotProp) return
    suppressClick.current = true
    const from = Math.min(current.anchor, current.current)
    // The slot under the finger is included, so a selection is never empty.
    const to = Math.max(current.anchor, current.current) + SNAP_MS
    onSelectSlotProp(current.registration, new Date(from), new Date(Math.min(to, dayEnd.getTime())))
  }

  // Percentage of the lane a booking occupies, clamped to the visible day.
  // A multi-day booking is clipped at both ends rather than dropped: it
  // genuinely does occupy this whole day.
  const geometry = (booking: Booking) => {
    const from = Math.max(new Date(booking.startsAt).getTime(), dayStart.getTime())
    const to = Math.min(new Date(booking.endsAt).getTime(), dayEnd.getTime())
    if (to <= from) return null
    return {
      left: ((from - dayStart.getTime()) / spanMs) * 100,
      width: ((to - from) / spanMs) * 100,
      clippedStart: new Date(booking.startsAt).getTime() < dayStart.getTime(),
      clippedEnd: new Date(booking.endsAt).getTime() > dayEnd.getTime(),
    }
  }

  const byAircraft = useMemo(() => {
    const map = new Map<string, Booking[]>()
    for (const booking of bookings) {
      const list = map.get(booking.registration) ?? []
      list.push(booking)
      map.set(booking.registration, list)
    }
    return map
  }, [bookings])

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      {/* Horizontally scrollable on a phone: the grid needs real width to
          stay readable, and squeezing 16 hours into 360px does not. */}
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: 720 }}>
          {/* Hour ruler */}
          <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ width: LABEL_WIDTH, flexShrink: 0, p: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {t('booking.aircraft')}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', position: 'relative' }}>
              {bands.map((band) => (
                <Box key={band.from} sx={bandStyle(band)} />
              ))}
              {hours.slice(0, -1).map((hour) => (
                <Box
                  key={hour}
                  sx={{
                    flex: 1,
                    borderLeft: 1,
                    borderColor: 'divider',
                    px: 0.5,
                    py: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {String(hour).padStart(2, '0')}
                  </Typography>
                </Box>
              ))}
              {sun.state !== 'normal' ? (
                <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', right: 8, top: 8 }}>
                  {t(sun.state === 'alwaysUp' ? 'calendar.midnightSun' : 'calendar.polarNight')}
                </Typography>
              ) : null}
              {sunMarks.map((mark) => (
                <Tooltip key={mark.iso} title={mark.label}>
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: `${((Date.parse(mark.iso) - dayStart.getTime()) / spanMs) * 100}%`,
                      transform: 'translateX(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.25,
                      color: 'warning.main',
                      bgcolor: 'background.paper',
                      px: 0.25,
                      borderRadius: 0.5,
                      lineHeight: 1,
                    }}
                  >
                    <Icon icon={mark.icon} width={16} />
                    <Typography variant="caption" sx={{ fontSize: 10 }}>
                      {formatTime(mark.iso)}
                    </Typography>
                  </Box>
                </Tooltip>
              ))}
            </Box>
          </Box>

          {aircraft.map((plane, index) => {
            const laneBookings = byAircraft.get(plane.registration) ?? []
            const reservations = laneBookings.filter((b) => b.kind === 'reservation')
            const options = laneBookings.filter((b) => b.kind === 'option')
            const color = aircraftColor(index)
            const status = statuses?.get(plane.registration)
            // Grounded today: nothing new can be booked on this lane (the
            // existing bookings stay, greyed and marked AOG).
            const aogToday = Boolean(status?.aog && overlapsAog(status.aog, dayKey, dayKey))
            // In maintenance today: likewise, and nothing is booked there.
            const maintenanceToday = Boolean(status?.maintenance && overlapsAog(status.maintenance, dayKey, dayKey))
            const onSelectSlot = aogToday || maintenanceToday ? undefined : onSelectSlotProp

            return (
              <Box
                key={plane.registration}
                sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider' }}
              >
                <Box sx={{ width: LABEL_WIDTH, flexShrink: 0, p: 1 }}>
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {plane.registration}
                    </Typography>
                    {status && status.light !== 'airworthy' ? (
                      <Tooltip
                        title={
                          status.light === 'aog'
                            ? t('calendar.aogBooking')
                            : status.restrictions.map((r) => t(`aircraftDefect.restrictions.${r}`)).join(', ') ||
                              t('calendar.restricted')
                        }
                      >
                        <Box
                          component="button"
                          type="button"
                          aria-label={t('calendar.openDefects')}
                          onClick={() => onOpenDefects?.(plane.registration)}
                          sx={{
                            display: 'inline-flex',
                            p: 0,
                            border: 0,
                            bgcolor: 'transparent',
                            cursor: 'pointer',
                            color: status.light === 'aog' ? 'error.main' : 'warning.main',
                          }}
                        >
                          <Icon icon={status.light === 'aog' ? 'mdi:hand-front-left' : 'mdi:alert-outline'} width={18} />
                        </Box>
                      </Tooltip>
                    ) : null}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {plane.aircraftType}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    position: 'relative',
                    height: LANE_HEIGHT,
                    cursor: onSelectSlot ? 'pointer' : 'default',
                    userSelect: 'none',
                    WebkitTouchCallout: 'none',
                  }}
                  onPointerDown={(event) => {
                    if (!onSelectSlot || event.button !== 0) return
                    // Bookings handle their own taps.
                    if (event.target !== event.currentTarget && (event.target as HTMLElement).closest('[data-booking]')) return
                    const rect = event.currentTarget.getBoundingClientRect()
                    const registration = plane.registration
                    const x = event.clientX
                    const begin = () => {
                      const at = floorAt(rect, x)
                      update({ registration, anchor: at, current: at })
                    }
                    if (event.pointerType === 'mouse') {
                      begin()
                    } else {
                      pending.current = { timer: window.setTimeout(begin, LONG_PRESS_MS), x, y: event.clientY }
                    }
                  }}
                  onPointerMove={(event) => {
                    const wait = pending.current
                    if (wait && !selectionRef.current) {
                      // Moved before the long press fired: the member is scrolling.
                      if (Math.abs(event.clientX - wait.x) > 8 || Math.abs(event.clientY - wait.y) > 8) {
                        window.clearTimeout(wait.timer)
                        pending.current = null
                      }
                      return
                    }
                    const current = selectionRef.current
                    if (!current || current.registration !== plane.registration) return
                    const rect = event.currentTarget.getBoundingClientRect()
                    update({ ...current, current: floorAt(rect, event.clientX) })
                  }}
                  onPointerUp={() => {
                    const current = selectionRef.current
                    // No movement: let the click handler treat it as a tap.
                    finish(current !== null && current.anchor !== current.current)
                    if (current && current.anchor === current.current) suppressClick.current = false
                  }}
                  onPointerCancel={() => finish(false)}
                  onPointerLeave={(event) => {
                    if (event.pointerType === 'mouse') finish(false)
                  }}
                  onClick={(event) => {
                    if (suppressClick.current) {
                      suppressClick.current = false
                      return
                    }
                    if (!onSelectSlot) return
                    const rect = event.currentTarget.getBoundingClientRect()
                    const ratio = (event.clientX - rect.left) / rect.width
                    const clicked = new Date(dayStart.getTime() + ratio * spanMs)
                    // Snap to the half hour: nobody books at 10:17.
                    clicked.setMinutes(clicked.getMinutes() < 30 ? 0 : 30, 0, 0)
                    onSelectSlot(plane.registration, clicked)
                  }}
                >
                  {/* Twilight and night, under everything else in the lane. */}
                  {bands.map((band) => (
                    <Box key={band.from} sx={bandStyle(band)} />
                  ))}

                  {/* Hour gridlines */}
                  {hours.slice(0, -1).map((hour, hourIndex) => (
                    <Box
                      key={hour}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: `${(hourIndex / (hours.length - 1)) * 100}%`,
                        borderLeft: 1,
                        borderColor: 'divider',
                      }}
                    />
                  ))}

                  {aogToday ? (
                    // The whole day is blocked: a red hatched all-day block.
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        display: 'grid',
                        placeItems: 'center',
                        color: 'error.main',
                        backgroundImage: `repeating-linear-gradient(45deg, ${alpha(theme.palette.error.main, 0.18)} 0 8px, ${alpha(
                          theme.palette.error.main,
                          0.06,
                        )} 8px 16px)`,
                      }}
                    >
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', opacity: 0.8 }}>
                        <Icon icon="mdi:hand-front-left" width={18} />
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          AOG
                        </Typography>
                      </Stack>
                    </Box>
                  ) : null}

                  {maintenanceToday ? (
                    // A yellow barber pole across the lane, labelled in words.
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        display: 'grid',
                        placeItems: 'center',
                        backgroundImage: BARBER_POLE,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{ alignItems: 'center', bgcolor: 'background.paper', color: 'text.primary', px: 1, py: 0.25, borderRadius: 1 }}
                      >
                        <Icon icon="mdi:wrench" width={16} />
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          {t('aircraft.inMaintenance')}
                        </Typography>
                      </Stack>
                    </Box>
                  ) : null}

                  {selection && selection.registration === plane.registration ? (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 2,
                        bottom: 2,
                        left: `${((Math.min(selection.anchor, selection.current) - dayStart.getTime()) / spanMs) * 100}%`,
                        width: `${((Math.abs(selection.current - selection.anchor) + SNAP_MS) / spanMs) * 100}%`,
                        bgcolor: alpha(theme.palette.primary.main, 0.25),
                        border: 2,
                        borderColor: 'primary.main',
                        borderRadius: 1,
                        pointerEvents: 'none',
                      }}
                    />
                  ) : null}

                  {reservations.map((booking) => {
                    const geo = geometry(booking)
                    if (!geo) return null
                    const maintenance = booking.isMaintenance
                    const onHold = bookingIsAog(booking, status)
                    return (
                      <BookingHover key={booking.bookingId} booking={booking}>
                        <Box
                          data-booking
                          onClick={(event) => {
                            event.stopPropagation()
                            peek.open(booking, event.currentTarget)
                          }}
                          sx={{
                            position: 'absolute',
                            left: `${geo.left}%`,
                            width: `${geo.width}%`,
                            top: 4,
                            bottom: 4 + OPTION_STRIP_HEIGHT,
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            // A maintenance block is grey and unmistakably
                            // not a member's booking.
                            // A maintenance booking carries the pole, an AOG one is
                            // greyed out, the rest take the aircraft's colour.
                            bgcolor: maintenance ? 'transparent' : onHold ? theme.palette.grey[500] : color,
                            backgroundImage: maintenance ? BARBER_POLE : undefined,
                            border: maintenance ? `1px solid ${alpha('#37474f', 0.55)}` : undefined,
                            color: maintenance
                              ? 'text.primary'
                              : theme.palette.getContrastText(onHold ? theme.palette.grey[500] : color),
                            opacity: onHold ? 0.7 : 1,
                            // Above the AOG hatching, so the booking is still readable.
                            zIndex: 1,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            // Square off whichever end runs past the edge
                            // of the visible day, so a clipped multi-day
                            // booking reads as continuing.
                            borderTopLeftRadius: geo.clippedStart ? 0 : undefined,
                            borderBottomLeftRadius: geo.clippedStart ? 0 : undefined,
                            borderTopRightRadius: geo.clippedEnd ? 0 : undefined,
                            borderBottomRightRadius: geo.clippedEnd ? 0 : undefined,
                          }}
                        >
                          <Typography variant="caption" noWrap sx={{ fontWeight: 600, display: 'block' }}>
                            <Icon icon={BOOKING_TYPE_ICONS[typeOf(booking)]} width={13} style={{ verticalAlign: -2, marginRight: 3 }} />
                            {onHold ? `AOG · ${booking.displayName}` : maintenance ? t('booking.maintenance') : booking.displayName}
                          </Typography>
                          <Typography variant="caption" noWrap sx={{ display: 'block', opacity: 0.85 }}>
                            {formatRange(booking.startsAt, booking.endsAt)}
                          </Typography>
                        </Box>
                      </BookingHover>
                    )
                  })}

                  {/* Options: a hatched strip along the bottom edge. They
                      never occupy the aircraft, so they never take lane
                      height away from the booking they are queued behind. */}
                  {options.map((option) => {
                    const geo = geometry(option)
                    if (!geo) return null
                    return (
                      <Tooltip
                        key={option.bookingId}
                        title={`${t('booking.option')}: ${option.displayName} · ${formatRange(option.startsAt, option.endsAt)}`}
                      >
                        <Box
                          data-booking
                          onClick={(event) => {
                            event.stopPropagation()
                            peek.open(option, event.currentTarget)
                          }}
                          sx={{
                            position: 'absolute',
                            left: `${geo.left}%`,
                            width: `${geo.width}%`,
                            bottom: 2,
                            height: OPTION_STRIP_HEIGHT,
                            borderRadius: 0.5,
                            cursor: 'pointer',
                            backgroundImage: `repeating-linear-gradient(45deg, ${
                              theme.palette.warning.main
                            } 0 4px, ${alpha(theme.palette.warning.light, 0.6)} 4px 8px)`,
                          }}
                        />
                      </Tooltip>
                    )
                  })}
                </Box>
              </Box>
            )
          })}
        </Box>
      </Box>

      <Stack direction="row" spacing={2} sx={{ p: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <LegendSwatch color={theme.palette.primary.main} label={t('calendar.legendBooked')} />
        <LegendSwatch
          label={t('calendar.legendOption')}
          backgroundImage={`repeating-linear-gradient(45deg, ${theme.palette.warning.main} 0 4px, ${alpha(
            theme.palette.warning.light,
            0.6,
          )} 4px 8px)`}
        />
        <LegendSwatch color={theme.palette.grey[500]} label={t('calendar.legendMaintenance')} />
        <LegendSwatch color={LIGHT_TINT.twilight} border label={t('calendar.legendTwilight')} />
        <LegendSwatch color={LIGHT_TINT.night} border label={t('calendar.legendNight')} />
        <LegendSwatch
          label={t('calendar.legendAog')}
          backgroundImage={`repeating-linear-gradient(45deg, ${alpha(theme.palette.error.main, 0.5)} 0 4px, ${alpha(
            theme.palette.error.main,
            0.2,
          )} 4px 8px)`}
        />
      </Stack>
      {peek.element}
    </Paper>
  )
}

function LegendSwatch({
  color,
  backgroundImage,
  border,
  label,
}: {
  color?: string
  backgroundImage?: string
  // For a pale tint that would vanish on the page.
  border?: boolean
  label: string
}) {
  return (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
      <Box sx={{ width: 16, height: 12, borderRadius: 0.5, bgcolor: color, backgroundImage, border: border ? 1 : undefined, borderColor: 'divider' }} />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  )
}
